import React, { useState, useEffect } from 'react';
import {Link, useNavigate} from 'react-router-dom';
import { useEventTracking } from '../hooks/useEventTracking';
import { useSession } from '../context/SessionContext';
import { useToast } from '../hooks/useToast';
import { ToastContainer } from '../components/ui/ToastContainer';
import { formatPhoneNumber } from '../hooks/useFormatters';
import { Helmet } from 'react-helmet-async';
import { CONTACT_DETAILS } from '@/config/contactDetails';

interface AssessmentAnswer {
    question_id: string;
    question: string;
    answer: string;
    answer_index?: number;
}

interface AssessmentResult {
    success: boolean;
    score: number;
    analysis: string;
    recommendations: string[];
}

interface FormData {
    full_name: string;
    company_name: string;
    role: string;
    email: string;
    phone: string;
    industry: string;
    organization_size: string;
    decision_making: string;
    data_systems: string[];
    data_utilization: string;
    prediction_capability: string;
    resource_challenges: string[];
    optimization_approach: string;
    knowledge_loss: string;
    expertise_capture: string;
    technology_integration: string;
    change_frequency: string;
    cost_of_issues: string;
    budget_authority: string;
    pilot_budget: string;
    prevention_value: string;
    current_solution: string;
    implementation_barriers: string[];
    critical_operations: string;
    timeline: string;
}

const questions = [
    {
        id: 1,
        title: "Let's start with some basic information",
        type: 'form',
        fields: [
            { name: 'full_name', label: 'Your Full Name *', type: 'text', required: true },
            { name: 'company_name', label: 'Organization Name *', type: 'text', required: true },
            { name: 'role', label: 'Your Role *', type: 'select', required: true, options: [
                    { value: '', label: 'Select your role' },
                    { value: 'operations_manager', label: 'Operations Manager' },
                    { value: 'department_head', label: 'Department Head/Director' },
                    { value: 'cto_cio', label: 'CTO/CIO/Technology Leader' },
                    { value: 'ceo_owner', label: 'CEO/Owner/Executive' },
                    { value: 'plant_facility_manager', label: 'Plant/Facility Manager' },
                    { value: 'clinical_admin', label: 'Clinical Administrator' },
                    { value: 'farm_manager', label: 'Farm/Agricultural Manager' },
                    { value: 'other', label: 'Other Leadership Role' }
                ]},
            { name: 'email', label: 'Email *', type: 'email', required: true },
            { name: 'phone', label: 'Phone', type: 'tel', required: false }
        ]
    },
    {
        id: 2,
        title: "What industry best describes your organization?",
        type: 'radio',
        name: 'industry',
        options: [
            { value: 'manufacturing', label: 'Manufacturing/Production' },
            { value: 'healthcare', label: 'Healthcare/Medical Services' },
            { value: 'energy', label: 'Energy/Utilities' },
            { value: 'forestry', label: 'Forestry/Natural Resources' },
            { value: 'industrial', label: 'Industrial/Heavy Equipment' },
            { value: 'other', label: 'Other' }
        ]
    },
    {
        id: 3,
        title: "What's the size of your organization?",
        type: 'radio',
        name: 'organization_size',
        options: [
            { value: 'small', label: 'Small (1-50 employees)' },
            { value: 'medium', label: 'Medium (51-250 employees)' },
            { value: 'large', label: 'Large (251-1000 employees)' },
            { value: 'enterprise', label: 'Enterprise (1000+ employees)' }
        ]
    },
    {
        id: 4,
        title: "How would you characterize your organization's decision-making approach?",
        type: 'radio',
        name: 'decision_making',
        options: [
            { value: 'mostly_reactive', label: 'Mostly reactive - we respond to issues as they arise' },
            { value: 'some_planning', label: 'Some planning - we have scheduled routines but still reactive to surprises' },
            { value: 'data_informed', label: 'Data-informed - we use historical data to plan ahead' },
            { value: 'predictive', label: 'Predictive - we anticipate issues before they occur' }
        ]
    },
    {
        id: 5,
        title: "What data systems do you currently have? (Check all that apply)",
        type: 'checkbox',
        name: 'data_systems',
        options: [
            { value: 'erp', label: 'ERP System' },
            { value: 'crm', label: 'CRM System' },
            { value: 'iot_sensors', label: 'IoT Sensors/Monitoring Devices' },
            { value: 'plc_scada', label: 'PLC/SCADA Systems' },
            { value: 'emr_ehr', label: 'EMR/EHR (Healthcare Records)' },
            { value: 'spreadsheets', label: 'Primarily Spreadsheets' },
            { value: 'manual_tracking', label: 'Manual Tracking/Paper Records' },
            { value: 'none', label: 'No formal data systems' }
        ]
    },
    {
        id: 6,
        title: "How well does your organization utilize the data it collects?",
        type: 'radio',
        name: 'data_utilization',
        options: [
            { value: 'not_collected', label: 'We don\'t collect much data systematically' },
            { value: 'collected_not_used', label: 'We collect data but rarely analyze it' },
            { value: 'basic_reports', label: 'We generate basic reports and dashboards' },
            { value: 'actionable_insights', label: 'We turn data into actionable insights regularly' }
        ]
    },
    {
        id: 7,
        title: "Can your organization predict problems or opportunities before they happen?",
        type: 'radio',
        name: 'prediction_capability',
        options: [
            { value: 'no_capability', label: 'No - we mainly react to what happens' },
            { value: 'intuition_based', label: 'Sometimes - based on experience and intuition' },
            { value: 'pattern_recognition', label: 'Often - we recognize patterns from historical data' },
            { value: 'systematic_prediction', label: 'Yes - we have systematic prediction methods' }
        ]
    },
    {
        id: 8,
        title: "What are your biggest resource allocation challenges? (Check all that apply)",
        type: 'checkbox',
        name: 'resource_challenges',
        options: [
            { value: 'staffing', label: 'Staffing levels and scheduling' },
            { value: 'inventory', label: 'Inventory/Supply management' },
            { value: 'capacity', label: 'Capacity planning and utilization' },
            { value: 'budget', label: 'Budget allocation and prioritization' },
            { value: 'equipment', label: 'Equipment/Asset management' },
            { value: 'timing', label: 'Timing of interventions or actions' },
            { value: 'none', label: 'We have resource allocation well optimized' }
        ]
    },
    {
        id: 9,
        title: "How do you currently optimize operations and make strategic decisions?",
        type: 'radio',
        name: 'optimization_approach',
        options: [
            { value: 'gut_feeling', label: 'Primarily based on experience and gut feeling' },
            { value: 'basic_metrics', label: 'Using basic metrics and KPIs' },
            { value: 'data_analysis', label: 'Regular data analysis and trend review' },
            { value: 'advanced_analytics', label: 'Advanced analytics and optimization tools' }
        ]
    },
    {
        id: 10,
        title: "Does your organization struggle with knowledge loss when experienced staff leave or retire?",
        type: 'radio',
        name: 'knowledge_loss',
        options: [
            { value: 'major_problem', label: 'Yes - major problem, significant expertise walks out the door' },
            { value: 'moderate_concern', label: 'Moderate concern - some knowledge loss impacts us' },
            { value: 'minor_issue', label: 'Minor issue - we have some documentation' },
            { value: 'not_concerned', label: 'Not concerned - our processes are well documented' }
        ]
    },
    {
        id: 11,
        title: "How does your organization capture and preserve expert knowledge?",
        type: 'radio',
        name: 'expertise_capture',
        options: [
            { value: 'informal_only', label: 'Informally - knowledge passed person-to-person' },
            { value: 'basic_documentation', label: 'Basic documentation and manuals' },
            { value: 'structured_process', label: 'Structured knowledge capture process' },
            { value: 'ai_powered', label: 'AI-powered knowledge management system' }
        ]
    },
    {
        id: 12,
        title: "How well do your existing systems integrate and share data?",
        type: 'radio',
        name: 'technology_integration',
        options: [
            { value: 'siloed', label: 'Very siloed - data trapped in separate systems' },
            { value: 'manual_integration', label: 'Some integration - requires manual effort' },
            { value: 'partial_automation', label: 'Partially automated data sharing' },
            { value: 'fully_integrated', label: 'Fully integrated - seamless data flow' }
        ]
    },
    {
        id: 13,
        title: "How often do unexpected issues disrupt your operations?",
        type: 'radio',
        name: 'change_frequency',
        options: [
            { value: 'daily', label: 'Daily or multiple times per week' },
            { value: 'weekly', label: 'Weekly' },
            { value: 'monthly', label: 'Monthly or every few months' },
            { value: 'rarely', label: 'Rarely - operations are very stable' }
        ]
    },
    {
        id: 14,
        title: "What's the typical cost impact when things go wrong?",
        type: 'radio',
        name: 'cost_of_issues',
        options: [
            { value: 'thousands', label: 'Thousands of dollars per incident' },
            { value: 'tens_thousands', label: 'Tens of thousands per incident' },
            { value: 'hundreds_thousands', label: 'Hundreds of thousands or more' },
            { value: 'minimal', label: 'Minimal financial impact' }
        ]
    },
    {
        id: 15,
        title: "What's your role in technology and solution purchasing decisions?",
        type: 'radio',
        name: 'budget_authority',
        options: [
            { value: 'final_decision', label: 'I make or approve final decisions' },
            { value: 'strong_influence', label: 'I have strong influence on decisions' },
            { value: 'provide_input', label: 'I provide input but others decide' },
            { value: 'no_influence', label: 'I have little influence on purchasing' }
        ]
    },
    {
        id: 16,
        title: "What would be a reasonable budget for a 3-6 month pilot program?",
        type: 'radio',
        name: 'pilot_budget',
        options: [
            { value: 'under_5k', label: 'Under $5,000' },
            { value: '5k_15k', label: '$5,000 - $15,000' },
            { value: '15k_30k', label: '$15,000 - $30,000' },
            { value: '30k_50k', label: '$30,000 - $50,000' },
            { value: 'over_50k', label: 'Over $50,000' }
        ]
    },
    {
        id: 17,
        title: "What would it be worth to predict and prevent issues 2-6 hours (or days) in advance?",
        type: 'radio',
        name: 'prevention_value',
        options: [
            { value: 'transformative', label: 'Transformative - would fundamentally improve operations' },
            { value: 'very_valuable', label: 'Very valuable - significant ROI potential' },
            { value: 'somewhat_valuable', label: 'Somewhat valuable - nice to have' },
            { value: 'not_much', label: 'Not much - we handle issues well reactively' }
        ]
    },
    {
        id: 18,
        title: "Are you currently using any predictive analytics or AI solutions?",
        type: 'radio',
        name: 'current_solution',
        options: [
            { value: 'yes_satisfied', label: 'Yes, and we\'re satisfied' },
            { value: 'yes_unsatisfied', label: 'Yes, but not completely satisfied' },
            { value: 'tried_discontinued', label: 'We tried one but discontinued it' },
            { value: 'no_never_tried', label: 'No, we\'ve never tried one' }
        ]
    },
    {
        id: 19,
        title: "If you've considered predictive solutions before, what were the barriers? (Check all that apply)",
        type: 'checkbox',
        name: 'implementation_barriers',
        options: [
            { value: 'too_expensive', label: 'Too expensive' },
            { value: 'too_complex', label: 'Too complex to implement' },
            { value: 'integration_concerns', label: 'Integration with existing systems' },
            { value: 'unclear_roi', label: 'Unclear ROI or value proposition' },
            { value: 'lack_expertise', label: 'Lack of internal expertise' },
            { value: 'change_resistance', label: 'Organizational resistance to change' },
            { value: 'not_applicable', label: 'We haven\'t considered this before' }
        ]
    },
    {
        id: 20,
        title: "Do you have one or two critical operations where prediction/optimization would be most valuable?",
        type: 'radio',
        name: 'critical_operations',
        options: [
            { value: 'yes_identified', label: 'Yes - we know exactly where to start' },
            { value: 'yes_multiple', label: 'Yes - we have several high-priority areas' },
            { value: 'all_important', label: 'Everything is equally important' },
            { value: 'need_help', label: 'We\'d need help identifying the best starting point' }
        ]
    },
    {
        id: 21,
        title: "If the right solution existed, what would be your timeline for implementation?",
        type: 'radio',
        name: 'timeline',
        options: [
            { value: 'immediately', label: 'Immediately - this is urgent' },
            { value: 'next_3_months', label: 'Within the next 3 months' },
            { value: 'next_6_months', label: 'Within the next 6 months' },
            { value: 'next_year', label: 'Sometime in the next year' },
            { value: 'just_exploring', label: 'Just exploring options for now' }
        ]
    }
];

export const AssessmentPage: React.FC = () => {
    const navigate = useNavigate();
    const { sessionId } = useSession();
    const { trackPageView, trackAssessmentAnswer, trackAssessmentComplete, trackClick, isSessionReady } = useEventTracking();
    const { toasts, removeToast, success, error, warning, info } = useToast();

    const [currentQuestion, setCurrentQuestion] = useState(1);
    const [formData, setFormData] = useState<Partial<FormData>>({
        data_systems: [],
        resource_challenges: [],
        implementation_barriers: []
    });
    const [result, setResult] = useState<AssessmentResult | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [startTime] = useState(Date.now());

    useEffect(() => {
        if (isSessionReady) {
            trackPageView('/assessment');
        }
    }, [isSessionReady]);

    const totalQuestions = questions.length;
    const progress = (currentQuestion / totalQuestions) * 100;

    const validateCurrentQuestion = (): boolean => {
        const question = questions[currentQuestion - 1];

        if (question.type === 'form') {
            const requiredFields = question.fields?.filter(field => field.required) || [];
            for (const field of requiredFields) {
                const value = formData[field.name as keyof FormData];
                if (!value || (typeof value === 'string' && !value.trim())) {
                    error('Required Field Missing', `Please fill in "${field.label}" before continuing.`);
                    return false;
                }
            }
        } else if (question.type === 'radio') {
            const value = formData[question.name as keyof FormData];
            if (!value) {
                warning('Please Select an Option', 'Choose an answer before continuing to the next question.');
                return false;
            }
        } else if (question.type === 'checkbox') {
            const values = formData[question.name as keyof FormData] as string[];
            if (!values || values.length === 0) {
                warning('Please Select at Least One Option', 'Choose at least one option before continuing.');
                return false;
            }
        }

        return true;
    };

    const handleInputChange = (name: string, value: string | string[]) => {
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (typeof value === 'string') {
            const question = questions.find(q => q.name === name);
            if (question) {
                trackAssessmentAnswer(name, question.title, value);
            }
        }
    };

    const handleCheckboxChange = (name: string, value: string, checked: boolean) => {
        const currentValues = (formData[name as keyof FormData] as string[]) || [];
        let newValues: string[];

        if (checked) {
            newValues = [...currentValues, value];
        } else {
            newValues = currentValues.filter(v => v !== value);
        }

        handleInputChange(name, newValues);
    };

    const nextQuestion = () => {
        if (validateCurrentQuestion() && currentQuestion < totalQuestions) {
            setCurrentQuestion(prev => prev + 1);
        }
    };

    const prevQuestion = () => {
        if (currentQuestion > 1) {
            setCurrentQuestion(prev => prev - 1);
        }
    };

    const submitAssessment = async () => {
        if (!sessionId || !validateCurrentQuestion()) return;

        if (isSubmitting) return;

        setIsSubmitting(true);
        const completionTime = Math.floor((Date.now() - startTime) / 1000);
        info('Processing Assessment', 'Analyzing your responses and calculating your readiness score...');

        try {
            const responses: AssessmentAnswer[] = [];

            Object.entries(formData).forEach(([key, value]) => {
                if (value) {
                    const question = questions.find(q => q.name === key) ||
                        questions.find(q => q.fields?.some(f => f.name === key));

                    let questionTitle = '';
                    let answerIndex: number | undefined = undefined;

                    if (!question) {
                        const formQuestion = questions.find(q => q.type === 'form');
                        const field = formQuestion?.fields?.find(f => f.name === key);
                        questionTitle = field?.label || key;
                    } else {
                        questionTitle = question.title;

                        if (question.type === 'radio' && question.options && typeof value === 'string') {
                            answerIndex = question.options.findIndex(option => option.value === value);
                            if (answerIndex === -1) answerIndex = undefined;
                        } else if (question.type === 'checkbox' && question.options && Array.isArray(value)) {
                            if (value.length > 0) {
                                answerIndex = question.options.findIndex(option => option.value === value[0]);
                                if (answerIndex === -1) answerIndex = undefined;
                            }
                        }
                    }

                    responses.push({
                        question_id: key,
                        question: questionTitle,
                        answer: Array.isArray(value) ? value.join(',') : value,
                        answer_index: answerIndex
                    });
                }
            });

            const submission = {
                session_id: sessionId,
                responses,
                completion_time_seconds: completionTime,
                contact_info: {
                    name: formData.full_name,
                    email: formData.email,
                    company: formData.company_name,
                    phone: formData.phone,
                    role: formData.role,
                    industry: formData.industry
                }
            };

            const response = await fetch('/api/assessment', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(submission),
            });

            if (!response.ok) {
                throw new Error('Failed to submit assessment');
            }

            const result: AssessmentResult = await response.json();
            setResult(result);
            success('Assessment Complete!', 'Check your email for detailed results within 2 business hours.');

            await trackAssessmentComplete(result.score, responses, completionTime);
        } catch (err) {
            console.error('Failed to submit assessment:', err);
            error(
                'Submission Failed',
                err instanceof Error
                    ? err.message
                    : 'Failed to submit your assessment. Please try again or contact us directly.'
            );
            setIsSubmitting(false);
        }
    };

    if (result) {
        return (
            <div className="min-h-screen bg-gray-50 py-12">
                <div className="max-w-4xl mx-auto px-4">
                    <div className="bg-white rounded-lg shadow-xl p-8 text-center">
                        <h2 className="text-3xl font-bold text-primary-800 mb-6">
                            Assessment Complete!
                        </h2>
                        <div className="mb-8">
                            <div className="text-6xl mb-4">🎯</div>
                            <h3 className="text-2xl font-semibold mb-4">
                                Readiness Score: <span className="text-primary-600">{result.score}</span>/100
                            </h3>
                            <p className="text-lg text-gray-700 mb-4">{result.analysis}</p>
                            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 text-left">
                                <h4 className="font-semibold text-blue-800 mb-2">What happens next:</h4>
                                <ul className="text-sm text-gray-700 space-y-1">
                                    <li>• We'll prepare a customized analysis based on your responses</li>
                                    <li>• You'll receive detailed results via email within 2 business hours</li>
                                    <li>• The report will include specific recommendations for your {formData.industry} operations</li>
                                    <li>• We'll show how the four-element approach (Monitor, Predict, Optimize, Preserve) applies to your situation</li>
                                </ul>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <Link
                                to="/contact?source=assessment"
                                onClick={() => trackClick('follow-up-email', 'Follow-up Email', 'link')}
                                className="inline-block bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors duration-300 mr-4"
                            >
                                Schedule Follow-up Call
                            </Link>
                            <button
                                onClick={() => {
                                    trackClick('return-home', 'Return Home', 'button');
                                    navigate('/');
                                }}
                                className="inline-block text-primary-600 hover:text-primary-800 underline"
                            >
                                Return to Main Page
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const question = questions[currentQuestion - 1];

    return (
        <>
            <Helmet>
                <title>{`Free Predictive Intelligence Assessment | ${CONTACT_DETAILS.company.name}`}</title>
                <meta
                    name="description"
                    content="Take our free cross-industry assessment to discover how the four-element methodology (Monitor, Predict, Optimize, Preserve) could transform your operations. Get personalized recommendations."
                />
                <meta name="keywords" content="predictive analytics assessment, readiness assessment, free AI assessment, cross-industry analytics evaluation" />
                <link rel="canonical" href="https://4castlabs.com/assessment" />
                <meta property="og:title" content={`Free Predictive Intelligence Assessment | ${CONTACT_DETAILS.company.name}`} />
                <meta property="og:description" content="Take our free assessment to discover how predictive intelligence could transform your operations. Get personalized recommendations." />
                <meta property="og:url" content="https://4castlabs.com/assessment" />
                <meta property="og:type" content="website" />
                <meta property="og:image" content="https://4castlabs.com/images/logo.png" />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "BreadcrumbList",
                        "itemListElement": [{
                            "@type": "ListItem",
                            "position": 1,
                            "name": "Home",
                            "item": "https://4castlabs.com/"
                        }, {
                            "@type": "ListItem",
                            "position": 2,
                            "name": "Assessment",
                            "item": "https://4castlabs.com/assessment"
                        }]
                    })}
                </script>
            </Helmet>
            <ToastContainer toasts={toasts} removeToast={removeToast} />
            <div className="bg-gray-50 min-h-screen">
                <div className="max-w-5xl mx-auto px-4 py-6">
                    {/* Header */}
                    <div className="text-center mb-6">
                        <h1 className="text-3xl font-bold text-primary-800 mb-3">
                            {CONTACT_DETAILS.company.name} Readiness Assessment
                        </h1>
                        <p className="text-lg text-gray-600 mb-2">
                            Discover how predictive intelligence could transform your operations
                        </p>
                        <p className="text-sm text-gray-500">This assessment takes 7-10 minutes</p>
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-6">
                        <div className="flex justify-between text-sm text-gray-600 mb-2">
                            <span>Progress</span>
                            <span>{currentQuestion} of {totalQuestions}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                                className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                    </div>

                    {/* Question Container */}
                    <div className="bg-white rounded-lg shadow-lg p-6" style={{ minHeight: '500px' }}>
                        <h2 className="text-xl font-semibold text-primary-800 mb-4">
                            {question.title}
                        </h2>

                        {question.type === 'form' && (
                            <div className="grid md:grid-cols-2 gap-4">
                                {question.fields?.map((field) => {
                                    if (field.name === 'phone') {
                                        return (
                                            <div key={field.name}>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    {field.label}
                                                </label>
                                                <input
                                                    type="tel"
                                                    value={(formData[field.name as keyof FormData] as string) || ''}
                                                    onChange={(e) => {
                                                        const formatted = formatPhoneNumber(e.target.value);
                                                        handleInputChange(field.name, formatted);
                                                    }}
                                                    required={field.required}
                                                    placeholder="+1 (555) 123-4567"
                                                    maxLength={18}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                                />
                                            </div>
                                        );
                                    }

                                    return (
                                        <div key={field.name}>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                {field.label}
                                            </label>
                                            {field.type === 'select' ? (
                                                <select
                                                    value={(formData[field.name as keyof FormData] as string) || ''}
                                                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                                                    required={field.required}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                                >
                                                    {field.options?.map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <input
                                                    type={field.type}
                                                    value={(formData[field.name as keyof FormData] as string) || ''}
                                                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                                                    required={field.required}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {question.type === 'radio' && (
                            <div
                                className={`grid ${question.options && question.options.length > 4 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'} gap-3`}>
                                {question.options?.map((option) => (
                                    <label
                                        key={option.value}
                                        className="flex items-center p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors min-h-[60px]"
                                    >
                                        <input
                                            type="radio"
                                            name={question.name}
                                            value={option.value}
                                            checked={(formData[question.name as keyof FormData] as string) === option.value}
                                            onChange={(e) => handleInputChange(question.name!, e.target.value)}
                                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 mr-3 flex-shrink-0"
                                        />
                                        <span className="text-gray-700">{option.label}</span>
                                    </label>
                                ))}
                            </div>
                        )}

                        {question.type === 'checkbox' && (
                            <div
                                className={`grid ${question.options && question.options.length > 4 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'} gap-3`}>
                                {question.options?.map((option) => {
                                    const currentValues = (formData[question.name as keyof FormData] as string[]) || [];
                                    const isChecked = currentValues.includes(option.value);

                                    return (
                                        <label
                                            key={option.value}
                                            className="flex items-center p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors min-h-[60px]"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isChecked}
                                                onChange={(e) => handleCheckboxChange(question.name!, option.value, e.target.checked)}
                                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mr-3 flex-shrink-0"
                                            />
                                            <span className="text-gray-700">{option.label}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Navigation Bar */}
                    <div className="sticky bottom-0 bg-white border-t-2 border-gray-200 p-4 mt-6 shadow-lg">
                        <div className="flex justify-between items-center">
                            <button
                                type="button"
                                onClick={prevQuestion}
                                disabled={currentQuestion === 1}
                                className={`font-semibold py-2 px-6 rounded-lg transition-colors duration-300 ${
                                    currentQuestion === 1
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
                                }`}
                            >
                                ← Previous
                            </button>

                            <div className="text-sm text-gray-600">
                                Question {currentQuestion} of {totalQuestions}
                            </div>

                            {currentQuestion === totalQuestions ? (
                                <button
                                    onClick={submitAssessment}
                                    disabled={isSubmitting}
                                    className="bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors duration-300 disabled:opacity-50"
                                >
                                    {isSubmitting ? 'Submitting...' : 'Get My Assessment Results'}
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={nextQuestion}
                                    className="bg-primary-600 hover:bg-primary-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors duration-300"
                                >
                                    Next →
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};